import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, Phone, MapPin, Clock, Search, ChevronDown, Heart } from "lucide-react";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import CartDrawer from "./CartDrawer";
import useWishlist from "@/hooks/useWishlist";

export default function Header() {
  const { cartCount, categories, settings } = useCart();
  const { ids: wishlistIds } = useWishlist();
  const wishlistCount = wishlistIds.length;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fallbacks match the previous hardcoded values so the header stays
  // populated on the first paint before /api/settings resolves.
  const s = settings || {};
  const addr = `${s.address_line1 || "578 Hyde Road"}, ${s.address_line2 || "Manchester M18 7EE"}`;
  const hours = `${s.hours_weekday || "Mon-Sat: 8AM-10PM"} | ${s.hours_weekend || "Sun: 8AM-8PM"}`;
  const phone = s.phone || "07404 133860";
  const phoneTel = phone.replace(/\s+/g, "");

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchOpen(false);
      setMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCatDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Top info bar */}
      {(settings?.top_banner_enabled ?? true) && (
        <div className="bg-slate-900 text-white py-2 px-4 text-sm hidden md:block">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-emerald-400" />
                {addr}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-emerald-400" />
                {hours}
              </span>
            </div>
            <a
              href={`tel:${phoneTel}`}
              className="flex items-center gap-2 hover:text-emerald-400 transition-colors"
              data-testid="top-bar-phone"
            >
              <Phone className="h-4 w-4" />
              {phone}
            </a>
          </div>
        </div>
      )}

      {/* Main header */}
      <header className="sticky top-0 z-50 glass-header border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/am06k7jp_FullLogo_NoBuffer%20%281%29.jpg" 
                alt="Gorton Superstore" 
                className="h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive("/") && location.pathname === "/"
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:text-emerald-600 hover:bg-slate-50"
                }`}
                data-testid="nav-home"
              >
                Home
              </Link>
              <Link
                to="/products"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  location.pathname === "/products"
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:text-emerald-600 hover:bg-slate-50"
                }`}
                data-testid="nav-shop-all"
              >
                Shop All
              </Link>

              {/* Categories dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    location.pathname.startsWith("/products/")
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-slate-600 hover:text-emerald-600 hover:bg-slate-50"
                  }`}
                  data-testid="nav-categories-dropdown"
                >
                  Categories
                  <ChevronDown className={`h-4 w-4 transition-transform ${catDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {catDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50" data-testid="categories-dropdown-menu">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/products/${cat.slug}`}
                        onClick={() => setCatDropdownOpen(false)}
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          location.pathname === `/products/${cat.slug}`
                            ? "bg-emerald-50 text-emerald-700 font-medium"
                            : "text-slate-700 hover:bg-slate-50 hover:text-emerald-600"
                        }`}
                        data-testid={`nav-cat-${cat.slug}`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Search bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center relative" data-testid="desktop-search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-48 lg:w-64 h-10 pl-10 pr-4 rounded-full border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                data-testid="desktop-search-input"
              />
              <Search className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none" />
            </form>

            {/* Right side actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Search toggle - Mobile */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden rounded-full h-10 w-10"
                onClick={() => setSearchOpen(!searchOpen)}
                data-testid="mobile-search-toggle"
              >
                <Search className="h-5 w-5 text-slate-700" />
              </Button>

              {/* Call Butcher Button - Desktop */}
              <a
                href="tel:07404133860"
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full text-sm font-medium hover:bg-red-700 transition-colors"
                data-testid="call-butcher-btn"
              >
                <Phone className="h-4 w-4 call-btn" />
                Call Butcher
              </a>

              {/* Wishlist */}
              <Link to="/wishlist" data-testid="wishlist-nav-link">
                <Button
                  variant="outline"
                  size="icon"
                  className="relative rounded-full h-10 w-10 border-slate-200"
                  aria-label="Wishlist"
                >
                  <Heart className={`h-5 w-5 ${wishlistCount > 0 ? "fill-rose-500 text-rose-500" : "text-slate-700"}`} />
                  {wishlistCount > 0 && (
                    <Badge
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-rose-600 text-white text-xs"
                      data-testid="wishlist-count"
                    >
                      {wishlistCount}
                    </Badge>
                  )}
                </Button>
              </Link>

              {/* Cart */}
              <CartDrawer>
                <Button
                  variant="outline"
                  size="icon"
                  className="relative rounded-full h-10 w-10 border-slate-200"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="h-5 w-5 text-slate-700" />
                  {cartCount > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-emerald-600 text-white text-xs"
                      data-testid="cart-count"
                    >
                      {cartCount}
                    </Badge>
                  )}
                </Button>
              </CartDrawer>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile search bar - slides down */}
        {searchOpen && (
          <div className="md:hidden border-t border-slate-200 px-4 py-3 bg-white" data-testid="mobile-search-bar">
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  autoFocus
                  className="w-full h-10 pl-10 pr-4 rounded-full border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  data-testid="mobile-search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <Button type="submit" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="mobile-search-submit">
                Search
              </Button>
            </form>
          </div>
        )}

        {/* Mobile menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-80 overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/am06k7jp_FullLogo_NoBuffer%20%281%29.jpg" 
                  alt="Gorton Superstore" 
                  className="h-12 w-auto"
                />
              </SheetTitle>
            </SheetHeader>

            {/* Mobile search */}
            <form onSubmit={handleSearch} className="mt-4 mb-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  data-testid="mobile-menu-search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
            </form>

            <div className="space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  location.pathname === "/"
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid="mobile-nav-home"
              >
                Home
              </Link>
              <Link
                to="/products"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  location.pathname === "/products"
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid="mobile-nav-shop-all"
              >
                Shop All
              </Link>
            </div>

            <Separator className="my-3" />
            <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Categories</p>
            <div className="space-y-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/products/${cat.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === `/products/${cat.slug}`
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`mobile-nav-${cat.slug}`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <Separator className="my-4" />
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-slate-600">
                <MapPin className="h-5 w-5 text-emerald-600" />
                <span className="text-sm">578 Hyde Road, Manchester M18 7EE</span>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Clock className="h-5 w-5 text-emerald-600" />
                <span className="text-sm">Mon-Sat: 8AM-10PM | Sun: 8AM-8PM</span>
              </div>
            </div>
            <SheetFooter className="mt-6">
              <a
                href="tel:07404133860"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-red-600 text-white rounded-xl font-medium"
                data-testid="mobile-call-butcher"
              >
                <Phone className="h-5 w-5" />
                Call Butcher: 07404 133860
              </a>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </header>
    </>
  );
}
